import { useMemo, useRef } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { PublicWorkerPool, RunWithPosition } from "types/generated";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "views/Account/WorkerPool/QueuedRunsView/constants";

import { SEARCH_PUBLIC_WORKER_POOL_SCHEDULABLE_RUNS } from "./gql";

const useSearchPublicQueuedRuns = () => {
  const { onError } = useTypedContext(FlashContext);
  const cachedEdges = useRef<RunWithPosition[]>([]);

  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [urlParams]
  );

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams, true);

    return [...(predicatesMap?.values() || [])];
  }, [urlParams]);

  const { error, loading, data, refetch, previousData } = usePolledQuery<{
    publicWorkerPool: PublicWorkerPool;
  }>(SEARCH_PUBLIC_WORKER_POOL_SCHEDULABLE_RUNS, {
    variables: {
      input: {
        predicates,
        fullTextSearch: searchInput,
        // "first" & "after" arguments are ignored by the server, no pagination is needed here
        first: null,
        after: null,
        ...(sortOptionFields && { orderBy: sortOptionFields }),
      },
    },
    pollingGroup: PollingIntervalGroups.Lists,
    // avoid request executing twice while fetchMore
    nextFetchPolicy: "cache-first",
    // APOLLO CLIENT UPDATE
  });

  const rawSearchRuns = useMemo(() => {
    const dataWorkerPool = data?.publicWorkerPool || previousData?.publicWorkerPool;
    if (dataWorkerPool) {
      const { searchSchedulableRuns } = dataWorkerPool;

      return searchSchedulableRuns;
    }

    return undefined;
  }, [data?.publicWorkerPool, previousData?.publicWorkerPool]);

  const memoizedEntities = useMemo(() => {
    const sourceEdges = rawSearchRuns?.edges.map((edge) => edge.node) || [];
    const edges = loading && !sourceEdges.length ? cachedEdges.current : sourceEdges;

    if (!loading) {
      cachedEdges.current = sourceEdges;
    }

    return edges;
  }, [rawSearchRuns?.edges, loading]);

  const handleRefetch = async () => {
    try {
      await refetch();
    } catch (error) {
      onError(error);
    }
  };

  return {
    entities: memoizedEntities,
    error,
    loading,
    isPageEmpty: !!(data && !memoizedEntities.length && !searchInput && predicates.length === 0),
    hasNoFilteringResults:
      !!data && !memoizedEntities.length && (!!searchInput || predicates.length > 0),
    refetch: handleRefetch,
  };
};

export default useSearchPublicQueuedRuns;
